"use client"

import { useState, useEffect, useRef } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useEvents } from '../context/EventsContext';
import { Helmet } from 'react-helmet-async'
import axios from "axios"
import config from "../config"
import { useAuth } from "../context/AuthContext"
import {
  FiMenu,
  FiX,
  FiBell,
  FiUser,
  FiHome,
  FiInfo,
  FiCalendar,
  FiGrid,
  FiLogOut,
  FiSettings,
  FiSearch,
  FiChevronDown,
  FiMessageSquare,
  FiBriefcase,
  FiCreditCard,
  FiMail,
  FiTrendingUp,
  FiLoader,
  FiSun,
  FiMoon,
} from "react-icons/fi"
import { motion, AnimatePresence } from "framer-motion"
import { useTheme } from "../context/ThemeContext"

const Navbar = ({ pageTitle, setPageTitle }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { isLoggedIn, user, logout, userProfile } = useAuth()
  const { theme, toggleTheme } = useTheme()

  // State management
  const [menuOpen, setMenuOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [totalResults, setTotalResults] = useState(0)
  const [recentNotifications, setRecentNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [notificationsLoading, setNotificationsLoading] = useState(false)

  // Refs for click outside detection
  const profileRef = useRef(null)
  const searchRef = useRef(null)
  const notificationsRef = useRef(null)

  // Environment variables
  const eventTitle = process.env.REACT_APP_EVENT_TITLE || "EventSawa"
  const eventDescription = process.env.REACT_APP_EVENT_DESCRIPTION || "We make events sawa"
  const faviconUrl = process.env.REACT_APP_FAVICON_URL || "/eventsawa.jpg"
  const brandLogo = `${process.env.PUBLIC_URL}/eventsawa.jpg`

  // Check if user is admin
  const isAdmin = user?.role === "admin"

  // Top navigation — kept lean so it stays on one line. Account/admin links live
  // in the profile dropdown; the logo already links Home.
  const navLinks = [
    { name: "Events", path: "/all-events", icon: <FiCalendar className="mr-2" /> },
    ...(isLoggedIn && user?.role === "service_provider"
      ? [{ name: "My Gigs", path: "/my-gigs", icon: <FiCalendar className="mr-2" /> }]
      : []),
    ...(isLoggedIn && user?.role === "event_organizer"
      ? [{ name: "My Events", path: "/my-events", icon: <FiCalendar className="mr-2" /> }]
      : []),
    { name: "Bids", path: "/bids", icon: <FiGrid className="mr-2" /> },
    // Workspaces are an organizer-only tool for now (service providers shouldn't see them).
    ...((isLoggedIn && (user?.role === "event_organizer" || isAdmin))
      ? [{ name: "Workspaces", path: "/communities", icon: <FiBriefcase className="mr-2" /> }] : []),
  ]

  // Update page title based on route
  useEffect(() => {
    const path = location.pathname
    let newTitle = "EventSawa - Professional Event Management"
    if (path === "/home") {
      newTitle = "EventSawa - Hme"
    } else if (path === "/events") {
      newTitle = "EventSawa - Events"
    } else if (path === "/profile") {
      newTitle = "EventSawa - Profile"
    } else if (path.startsWith("/events/")) {
      newTitle = "EventSawa - Event Details"
    }
    setPageTitle(newTitle)
  }, [location, setPageTitle])

  // Close all dropdowns when user logs out
  useEffect(() => {
    if (!isLoggedIn) {
      setMenuOpen(false)
      setProfileOpen(false)
      setSearchOpen(false)
      setNotificationsOpen(false)
    }
  }, [isLoggedIn])

  // Fetch notifications
  const fetchNotifications = async () => {
    if (!isLoggedIn) return
    setNotificationsLoading(true)
    try {
      const response = await axios.get(`${config.API_BASE_URL}/notifications/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      })
      const notifications = response.data || []
      const unreadNotifications = notifications.filter((notif) => !notif.is_read && !notif.is_hidden)
      setRecentNotifications(notifications.slice(0, 5))
      setUnreadCount(unreadNotifications.length)
    } catch (error) {
      // console.error("Failed to fetch notifications:", error)
      // Set mock data for demo
      setRecentNotifications([
        {
          id: 1,
          title: "New bid received",
          message: "You have a new bid for your event",
          type: "bid",
          is_read: false,
          created_at: new Date().toISOString(),
        },
        {
          id: 2,
          title: "Event reminder",
          message: "Your event starts in 2 hours",
          type: "event",
          is_read: false,
          created_at: new Date().toISOString(),
        },
        {
          id: 3,
          title: "Payment received",
          message: "Payment of $500 has been processed",
          type: "payment",
          is_read: true,
          created_at: new Date(Date.now() - 86400000).toISOString(),
        },
      ])
      setUnreadCount(2)
    } finally {
      setNotificationsLoading(false)
    }
  }

  // Get events from context
  const { events: allEvents, loading: eventsLoading } = useEvents();

  // Cache for search results
  const searchCache = useRef({});
  const [localEvents, setLocalEvents] = useState([]);

  // Update local events when context events change
  useEffect(() => {
    if (Array.isArray(allEvents)) {
      setLocalEvents(allEvents);
    }
  }, [allEvents]);

  // Search functionality
  const performSearch = (query) => {
    const searchTerm = query.trim().toLowerCase();

    if (!searchTerm) {
      setSearchResults([]);
      return;
    }

    // Check cache first
    if (searchCache.current[searchTerm]) {
      setSearchResults(searchCache.current[searchTerm]);
      return;
    }

    setSearchLoading(true);

    try {
      // Client-side search on local events
      const filteredEvents = localEvents.filter(event => {
        if (!event) return false;
        const searchableText = [
          event.title,
          event.description,
          event.location,
          event.venue,
          event.name,
          ...(Array.isArray(event.keywords) ? event.keywords : [])
        ].join(' ').toLowerCase();

        return searchableText.includes(searchTerm);
      });

      // Update total results count
      setTotalResults(filteredEvents.length);

      // Format results (limit to 5 for display)
      const formattedResults = filteredEvents.slice(0, 5).map(item => ({
        ...item,
        type: 'event',
        id: item.id || item._id,
        slug: item.slug || item.id || item._id,
        title: item.title || item.name || 'Untitled Event',
        description: item.description || '',
        location: item.location || item.venue || 'No location specified'
      }));

      // Cache the results
      searchCache.current[searchTerm] = formattedResults;

      // Update state
      setSearchResults(formattedResults);
    } catch (error) {
      // console.error('Search error:', error);
      // Fallback to empty array if there's an error with local search
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }

  // Mark notification as read
  const markAsRead = async (notificationId, e) => {
    e.stopPropagation()
    try {
      // Try multiple possible endpoints
      const endpoints = [
        `${config.API_BASE_URL}/notifications/${notificationId}/read/`,
        `${config.API_BASE_URL}/notifications/${notificationId}/mark-read/`,
        `${config.API_BASE_URL}/notifications/mark-read/${notificationId}/`,
        `${config.API_BASE_URL}/notifications/${notificationId}/`,
      ]
      let success = false
      for (const endpoint of endpoints) {
        try {
          await axios.patch(
            endpoint,
            { is_read: true },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("access_token")}`,
              },
            },
          )
          success = true
          break
        } catch (err) {
          continue
        }
      }

      // Update local state regardless of API success for better UX
      setRecentNotifications((prev) =>
        prev.map((notif) => (notif.id === notificationId ? { ...notif, is_read: true } : notif)),
      )
      setUnreadCount((prev) => Math.max(0, prev - 1))
      if (!success) {
        // console.warn("Could not mark notification as read via API, but updated locally")
      }
    } catch (error) {
      // console.error("Failed to mark notification as read:", error)
      // Still update locally for better UX
      setRecentNotifications((prev) =>
        prev.map((notif) => (notif.id === notificationId ? { ...notif, is_read: true } : notif)),
      )
      setUnreadCount((prev) => Math.max(0, prev - 1))
    }
  }

  // Auto-refresh notifications
  useEffect(() => {
    if (isLoggedIn) {
      fetchNotifications()
      const interval = setInterval(fetchNotifications, 30000) // Refresh every 30 seconds
      return () => clearInterval(interval)
    }
  }, [isLoggedIn])

  // Handle search input changes with debouncing
  useEffect(() => {
    // Don't search if we're still loading events
    if (eventsLoading) return;

    const debounceTimer = setTimeout(() => {
      if (searchQuery && searchOpen) {
        performSearch(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 150); // Shorter debounce since we're searching locally

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [searchQuery, searchOpen, localEvents, eventsLoading]);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true)
      } else {
        setScrolled(false)
      }
    }
    window.addEventListener("scroll", handleScroll)
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false)
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchOpen(false)
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Keyboard handling
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setSearchOpen(false)
        setNotificationsOpen(false)
        setProfileOpen(false)
        setMenuOpen(false)
        setSearchQuery("")
        setSearchResults([])
      }
      if (e.key === "Enter" && searchQuery && searchResults.length > 0) {
        const firstResult = searchResults[0]
        navigate(firstResult.type === "event" ? `/events/${firstResult.slug}` : `/bids/${firstResult.id}`)
        setSearchOpen(false)
        setSearchQuery("")
      }
      // Cmd/Ctrl + K to open search
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [searchQuery, searchResults, navigate])

  // Close mobile menu when route changes
  useEffect(() => {
    setMenuOpen(false)
  }, [location])

  // Check if link is active
  const isActive = (path) => {
    return location.pathname === path
  }

  // Helper functions
  const getNotificationIcon = (type) => {
    switch (type) {
      case "bid":
        return <FiBriefcase className="w-4 h-4" />
      case "event":
        return <FiCalendar className="w-4 h-4" />
      case "message":
        return <FiMail className="w-4 h-4" />
      case "payment":
        return <FiTrendingUp className="w-4 h-4" />
      default:
        return <FiBell className="w-4 h-4" />
    }
  }

  const getNotificationColor = (type) => {
    switch (type) {
      case "bid":
        return "bg-blue-100 text-blue-600 dark:bg-blue-900/40"
      case "event":
        return "bg-purple-100 text-purple-600 dark:bg-purple-900/40"
      case "message":
        return "bg-green-100 text-green-600 dark:bg-green-900/40"
      case "payment":
        return "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40"
      default:
        return "bg-gray-100 text-gray-600 dark:text-gray-300 dark:bg-gray-800"
    }
  }

  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return "U"
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={eventDescription} />
        <link rel="icon" href={faviconUrl} />
      </Helmet>

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled
          ? "bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl shadow-xl border-b border-gray-200 dark:border-gray-700/50 py-2"
          : "bg-white/90 dark:bg-gray-900/90 backdrop-blur-md py-4"
          }`}
      >
        <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 w-full">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 w-full">
            {/* Enhanced Logo */}
            <div className="flex items-center justify-between w-full sm:w-auto">
              <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group flex-shrink-0">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-gray-200 dark:border-gray-700 flex items-center justify-center flex-shrink-0">
                  <img
                    src={brandLogo}
                    alt={eventTitle}
                    className="w-6 h-6 sm:w-8 sm:h-8 rounded-full"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent whitespace-nowrap">
                    {eventTitle}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium hidden sm:block">Professional Events</span>
                </div>
              </Link>
              {/* Mobile menu button */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="sm:hidden p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {menuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
              </button>
            </div>

            {/* Desktop Navigation - Show on medium screens and up */}
            <nav className="hidden md:flex items-center space-x-2 lg:space-x-3 ml-4 lg:ml-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${isActive(link.path)
                    ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/20"
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-indigo-600 dark:hover:text-indigo-300"
                    }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Desktop Right Section */}
            <div className="hidden sm:flex items-center space-x-2">
              {/* Dark mode toggle */}
              <button
                onClick={toggleTheme}
                aria-label="Toggle dark mode"
                title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                className="p-3 rounded-xl text-gray-600 dark:text-gray-300 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-gray-800 transition-all duration-300"
              >
                {theme === "dark" ? <FiSun className="w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
              </button>

              {/* Search functionality */}
              <div ref={searchRef} className="relative">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search events..."
                    className="w-64 pl-10 pr-4 py-2.5 text-sm bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 border border-transparent rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white dark:focus:bg-gray-900 transition-colors"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setSearchOpen(true)}
                    onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
                  />
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  {searchLoading && (
                    <FiLoader className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 animate-spin" />
                  )}
                </div>

                {searchOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 z-50 overflow-hidden">
                    {searchLoading ? (
                      <div className="p-4 text-center">
                        <div className="flex items-center justify-center">
                          <FiLoader className="animate-spin mr-2" />
                          <span>Searching...</span>
                        </div>
                      </div>
                    ) : searchResults.length > 0 ? (
                      <>
                        <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 border-b">
                          Showing {Math.min(5, totalResults)} of {totalResults} {totalResults === 1 ? 'event' : 'events'}
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                          {searchResults.map((result, index) => (
                            <Link
                              key={`${result.type}-${result.id || index}`}
                              to={`/events/${result.slug || result.id}`}
                              className="block p-3 hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors"
                              onClick={() => {
                                setSearchOpen(false);
                                setSearchQuery('');
                              }}
                            >
                              <div className="flex items-center space-x-3">
                                <div className="p-2 rounded-lg bg-indigo-100 text-indigo-600">
                                  <FiCalendar className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{result.title}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                                    {result.location || 'No location specified'}
                                  </p>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </>
                    ) : searchQuery ? (
                      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                        <p>No results found for "{searchQuery}"</p>
                      </div>
                    ) : (
                      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                        <p>Type to search for events</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Enhanced Notifications */}
              {isLoggedIn && (
                <div className="relative" ref={notificationsRef}>
                  <button
                    onClick={() => setNotificationsOpen(!notificationsOpen)}
                    className="relative p-3 rounded-xl text-gray-600 dark:text-gray-300 hover:text-indigo-600 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 transition-all duration-300 hover:shadow-md hover:scale-110"
                  >
                    <FiBell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold animate-bounce shadow-lg">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </button>
                  <AnimatePresence>
                    {notificationsOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute right-0 mt-3 w-96 bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-gray-100 dark:border-gray-700 z-50 backdrop-blur-xl overflow-hidden"
                      >
                        <div className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30">
                          <div className="flex items-center justify-between">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Notifications</h3>
                            {unreadCount > 0 && (
                              <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white text-sm px-3 py-1 rounded-full font-bold">
                                {unreadCount} new
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                          {notificationsLoading ? (
                            <div className="p-8 text-center">
                              <div className="w-8 h-8 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto"></div>
                            </div>
                          ) : recentNotifications.length > 0 ? (
                            <div className="divide-y divide-gray-100">
                              {recentNotifications.map((notification) => (
                                <div
                                  key={notification.id}
                                  className={`p-5 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 transition-all duration-200 cursor-pointer ${!notification.is_read ? "bg-indigo-50/30 border-l-4 border-indigo-500" : ""
                                    }`}
                                  onClick={() => {
                                    if (!notification.is_read) {
                                      markAsRead(notification.id, { stopPropagation: () => { } })
                                    }
                                  }}
                                >
                                  <div className="flex items-start space-x-4">
                                    <div
                                      className={`p-3 rounded-xl ${getNotificationColor(notification.type)} shadow-sm`}
                                    >
                                      {getNotificationIcon(notification.type)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center space-x-2">
                                        <p className="text-base font-semibold text-gray-900 dark:text-white">{notification.title}</p>
                                        {!notification.is_read && (
                                          <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
                                        )}
                                      </div>
                                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">{notification.message}</p>
                                      <div className="flex items-center justify-between mt-3">
                                        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                          {new Date(notification.created_at).toLocaleDateString()}
                                        </p>
                                        {!notification.is_read && (
                                          <button
                                            onClick={(e) => markAsRead(notification.id, e)}
                                            className="text-indigo-600 hover:text-indigo-800 text-xs font-bold bg-indigo-100 px-2 py-1 rounded-lg hover:bg-indigo-200 transition-colors"
                                          >
                                            Mark read
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="p-12 text-center">
                              <FiBell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                              <p className="text-base text-gray-500 dark:text-gray-400 font-medium">No notifications yet</p>
                              <p className="text-sm text-gray-400 mt-1">We'll notify you when something happens</p>
                            </div>
                          )}
                        </div>
                        {recentNotifications.length > 0 && (
                          <div className="p-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50">
                            <Link
                              to="/notifications"
                              onClick={() => setNotificationsOpen(false)}
                              className="w-full text-center text-indigo-600 hover:text-indigo-800 text-sm font-bold block py-2 px-4 rounded-xl hover:bg-indigo-100 transition-colors"
                            >
                              View all notifications
                            </Link>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* User Menu - Different behavior based on screen size */}
              {!isLoggedIn ? (
                <>
                  {/* Large screens - Login + Get Started CTA */}
                  <div className="hidden md:flex items-center gap-2">
                    <Link
                      to="/login"
                      className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors"
                    >
                      Log in
                    </Link>
                    <Link
                      to="/register"
                      className="group inline-flex items-center gap-1.5 pl-5 pr-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full shadow-md hover:shadow-lg hover:shadow-indigo-500/30 hover:scale-[1.03] transition-all duration-300"
                    >
                      Get Started
                      <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </Link>
                  </div>

                  {/* Small screens - Show user icon with dropdown */}
                  <div className="md:hidden relative" ref={profileRef}>
                    <button
                      onClick={() => setProfileOpen(!profileOpen)}
                      className="p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      aria-label="User menu"
                    >
                      <FiUser className="w-5 h-5" />
                    </button>

                    {/* Dropdown menu */}
                    {profileOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-1 z-50">
                        <Link
                          to="/login"
                          onClick={() => setProfileOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                          Log In
                        </Link>
                        <Link
                          to="/register"
                          onClick={() => setProfileOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                          Create Account
                        </Link>
                      </div>
                    )}
                  </div>
                </>
              ) : isLoggedIn && user ? (
                <div className="relative" ref={profileRef}>
                  <button
                    onClick={() => setProfileOpen(!profileOpen)}
                    className="flex items-center space-x-3 p-2 rounded-2xl hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 transition-all duration-300 hover:shadow-lg group"
                    aria-label="User menu"
                  >
                    <div className="relative">
                      <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center overflow-hidden border-3 border-white shadow-lg group-hover:scale-110 transition-transform duration-300">
                        {userProfile?.profile_picture ? (
                          <img
                            src={userProfile.profile_picture || "/placeholder.svg?height=40&width=40"}
                            alt={`${user.first_name} ${user.last_name}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-white font-bold text-sm">
                            {getUserInitials(`${user.first_name} ${user.last_name}`)}
                          </span>
                        )}
                      </div>
                      {/* Online Status Indicator */}
                      <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-400 rounded-full border-2 border-white animate-pulse shadow-sm"></div>
                    </div>
                    <div className="hidden sm:block text-left">
                      <p className="text-sm font-bold text-gray-900 dark:text-white group-hover:text-indigo-600 transition-colors">
                        {`${user.first_name} ${user.last_name}` || "User"}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Online</p>
                    </div>
                    <FiChevronDown
                      className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-all duration-300 group-hover:text-indigo-600 ${profileOpen ? "transform rotate-180" : ""
                        }`}
                    />
                  </button>
                  <AnimatePresence>
                    {profileOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute right-0 mt-3 w-80 bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden z-50 backdrop-blur-xl"
                      >
                        {/* Profile Header */}
                        <div className="p-6 bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-500 text-white">
                          <div className="flex items-center space-x-4">
                            <div className="relative">
                              <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center overflow-hidden border-2 border-white/30">
                                {userProfile?.profile_picture ? (
                                  <img
                                    src={userProfile.profile_picture || "/placeholder.svg?height=64&width=64"}
                                    alt={`${user.first_name} ${user.last_name}` || "Profile"}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <span className="text-white font-bold text-xl">
                                    {getUserInitials(`${user.first_name} ${user.last_name}`)}
                                  </span>
                                )}
                              </div>
                              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-400 rounded-full border-2 border-white animate-pulse"></div>
                            </div>
                            <div className="flex-1">
                              <p className="text-lg font-bold">
                                {`${user.first_name} ${user.last_name}` || "Loading..."}
                              </p>
                              <p className="text-sm text-white/80 truncate">{user?.email || ""}</p>
                              <div className="flex items-center space-x-2 mt-2">
                                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                <span className="text-xs font-medium text-white/90">Online</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Menu Items */}
                        <div className="p-2">
                          <Link
                            to={`/profile/${userProfile?.slug}`}
                            className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:text-indigo-600 rounded-2xl transition-all duration-200 group"
                          >
                            <FiUser className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-indigo-600 transition-colors" />
                            <span className="font-medium">My Profile</span>
                          </Link>
                          <Link
                            to="/dashboard"
                            className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:text-indigo-600 rounded-2xl transition-all duration-200 group"
                          >
                            <FiGrid className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-indigo-600 transition-colors" />
                            <span className="font-medium">Dashboard</span>
                          </Link>
                          {user?.role === "event_organizer" && (
                            <Link
                              to="/wallet"
                              className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:text-indigo-600 rounded-2xl transition-all duration-200 group"
                            >
                              <FiCreditCard className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-indigo-600 transition-colors" />
                              <span className="font-medium">My Wallet</span>
                            </Link>
                          )}
                          <Link
                            to="/notifications"
                            className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:text-indigo-600 rounded-2xl transition-all duration-200 group"
                          >
                            <FiMessageSquare className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-indigo-600 transition-colors" />
                            <span className="font-medium">Messages</span>
                            {unreadCount > 0 && (
                              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                                {unreadCount}
                              </span>
                            )}
                          </Link>
                          {isAdmin && (
                            <>
                              <Link
                                to="/admin"
                                className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:text-indigo-600 rounded-2xl transition-all duration-200 group"
                              >
                                <FiSettings className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-indigo-600 transition-colors" />
                                <span className="font-medium">Admin</span>
                              </Link>
                              <Link
                                to="/admin/withdrawals"
                                className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:text-indigo-600 rounded-2xl transition-all duration-200 group"
                              >
                                <FiCreditCard className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-indigo-600 transition-colors" />
                                <span className="font-medium">Withdrawals</span>
                              </Link>
                            </>
                          )}
                          <Link
                            to="/about"
                            className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:text-indigo-600 rounded-2xl transition-all duration-200 group"
                          >
                            <FiInfo className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-indigo-600 transition-colors" />
                            <span className="font-medium">About</span>
                          </Link>
                        </div>
                        {/* Sign Out */}
                        <div className="p-2 border-t border-gray-100 dark:border-gray-700">
                          <button
                            onClick={logout}
                            className="flex w-full items-center space-x-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 rounded-2xl transition-all duration-200 group"
                          >
                            <FiLogOut className="w-5 h-5 group-hover:scale-110 transition-transform" />
                            <span className="font-medium">Sign out</span>
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="hidden">
                  {/* This is a fallback that won't be rendered */}
                </div>
              )}

              {/* Mobile Menu Toggle */}
              <button
                className="md:hidden p-3 rounded-xl text-gray-700 dark:text-gray-200 hover:text-indigo-600 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 transition-all duration-300 hover:shadow-md"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Toggle menu"
              >
                {menuOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Enhanced Mobile Menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden overflow-hidden bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-t border-gray-200 dark:border-gray-700/50"
            >
              <div className="container mx-auto px-4 py-6">
                <nav className="flex flex-col space-y-2">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`px-4 py-4 rounded-2xl flex items-center transition-all duration-300 ${isActive(link.path)
                        ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg"
                        : "text-gray-700 dark:text-gray-200 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:text-indigo-600"
                        }`}
                    >
                      {link.icon}
                      <span className="font-medium">{link.name}</span>
                    </Link>
                  ))}
                  <button
                    onClick={toggleTheme}
                    className="px-4 py-4 rounded-2xl flex items-center text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
                  >
                    {theme === "dark" ? <FiSun className="mr-2" /> : <FiMoon className="mr-2" />}
                    <span className="font-medium">{theme === "dark" ? "Light mode" : "Dark mode"}</span>
                  </button>
                </nav>
                {isLoggedIn ? (
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center px-4 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-2xl">
                      <div className="flex-shrink-0 relative">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center overflow-hidden">
                          {userProfile?.profile_picture ? (
                            <img
                              src={userProfile.profile_picture || "/placeholder.svg?height=48&width=48"}
                              alt={`${user.first_name} ${user.last_name}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-white font-bold">
                              {getUserInitials(`${user.first_name} ${user.last_name}`)}
                            </span>
                          )}
                        </div>
                        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-400 rounded-full border-2 border-white animate-pulse"></div>
                      </div>
                      <div className="ml-4 flex-1">
                        <p className="text-base font-bold text-gray-900 dark:text-white">
                          {`${user.first_name} ${user.last_name}` || "Loading..."}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{user?.email || ""}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Online</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 space-y-2">
                      <Link
                        to={`/profile/${userProfile?.slug}`}
                        className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:text-indigo-600 rounded-2xl font-medium transition-all duration-200"
                      >
                        My Profile
                      </Link>
                      <Link
                        to="/dashboard"
                        className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:text-indigo-600 rounded-2xl font-medium transition-all duration-200"
                      >
                        Dashboard
                      </Link>
                      <button
                        onClick={logout}
                        className="block w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 rounded-2xl font-medium transition-all duration-200"
                      >
                        Sign out
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 flex flex-col space-y-3">
                    <Link
                      to="/login"
                      className="px-4 py-3 text-center text-gray-700 dark:text-gray-200 border-2 border-gray-300 dark:border-gray-600 rounded-2xl hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:border-indigo-300 hover:text-indigo-600 font-medium transition-all duration-300"
                    >
                      Log in
                    </Link>
                    <Link
                      to="/register"
                      className="px-4 py-3 text-center text-white bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-600 rounded-2xl font-semibold shadow-lg transition-all duration-300"
                    >
                      Get Started
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Spacer to prevent content from being hidden under fixed navbar */}
      <div className={`${scrolled ? "h-20" : "h-24"} transition-all duration-500`}></div>
    </>
  )
}

export default Navbar
